/**
 * Acting in one client from a master account session, one request at a time: the request carries
 * the client's Account Node ID in `OFFICE_CLIENT_ACCOUNT_HEADER` and the session never changes.
 */
import { ApolloLink, CombinedGraphQLErrors, Observable, ServerError } from '@apollo/client';
import { OFFICE_CLIENT_ACCOUNT_HEADER } from '@insio/shared-types';
import { GraphQLErrorCode } from '@insio/validation';
import { codesInBody } from './auth-error';

const HTTP_FORBIDDEN = 403;

/**
 * Adds the office client header to an operation whose context names a client. HTTP only: the
 * server refuses the header on a WebSocket, so this link must never sit in front of the ws link.
 */
export const officeClientLink = new ApolloLink((operation, forward) => {
  const { officeClientAccountId } = operation.getContext();
  // An empty id is still sent, so the server refuses it instead of running in the master account.
  if (typeof officeClientAccountId === 'string') {
    operation.setContext(({ headers = {} }) => ({
      headers: { ...headers, [OFFICE_CLIENT_ACCOUNT_HEADER]: officeClientAccountId }
    }));
  }
  return forward(operation);
});

/** Message of the error {@link officeClientWsGuardLink} raises. */
export const OFFICE_CLIENT_OVER_WS_MESSAGE =
  'An operation in a client cannot run over the WebSocket: the office client header is HTTP only.';

/**
 * Sits in front of the ws link and errors an operation whose context names a client, so it never
 * runs silently in the master account.
 */
export const officeClientWsGuardLink = new ApolloLink((operation, forward) => {
  if (typeof operation.getContext().officeClientAccountId === 'string') {
    return new Observable(observer => observer.error(new Error(OFFICE_CLIENT_OVER_WS_MESSAGE)));
  }
  return forward(operation);
});

/**
 * Operation context naming the client. Deduplication is off: Apollo keys an in-flight request by
 * query and variables only, so two clients' identical reads would otherwise share one answer.
 */
export function officeClientOperationContext(clientAccountId: string) {
  return { officeClientAccountId: clientAccountId, queryDeduplication: false };
}

/**
 * Query options for a read in one client, spread into `useQuery` or `client.query`.
 * `no-cache` keeps the client's root fields (account-scoped answers) out of the office's cache.
 */
export function officeClientContext(clientAccountId: string) {
  return {
    context: officeClientOperationContext(clientAccountId),
    fetchPolicy: 'no-cache'
  } as const;
}

/**
 * Mutation options for a write in one client, for `useMutation` or a single call. `no-cache` keeps
 * the answer's entities out of the office's cache; an `update` callback still must not write one.
 */
export function officeClientMutationOptions(clientAccountId: string) {
  return {
    context: officeClientOperationContext(clientAccountId),
    fetchPolicy: 'no-cache'
  } as const;
}

/** Headers for a REST request (`fetch`) in one client. */
export function officeClientHeaders(clientAccountId: string): Record<string, string> {
  return { [OFFICE_CLIENT_ACCOUNT_HEADER]: clientAccountId };
}

const OFFICE_CLIENT_REFUSAL_CODES: ReadonlySet<unknown> = new Set([
  GraphQLErrorCode.OFFICE_CLIENT_CONTEXT_REFUSED,
  GraphQLErrorCode.OFFICE_CLIENT_SCOPE_REQUIRED
]);

/**
 * True when the server refused a request carrying the office client header: the client is not
 * one the office may act in, or the operation is not offered from the office. Never a sign-out.
 */
export function isOfficeClientRefusal(error: unknown): boolean {
  const codes = CombinedGraphQLErrors.is(error)
    ? error.errors.map(e => e.extensions?.code)
    : ServerError.is(error) && error.statusCode === HTTP_FORBIDDEN
      ? codesInBody(error.bodyText)
      : [];
  return codes.some(code => OFFICE_CLIENT_REFUSAL_CODES.has(code));
}
